import { memo, useCallback, useEffect } from 'react';
import PageTitle from '@/shared/components/PageTitle';
import { usePageStore } from '@/features/pages/stores/usePageStore';
import Editor from '@/shared/components/Editor';
import Spinner from '@/shared/components/ui/spinner';
import { useQuery } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import remoteDbService from '@/core/database/RemoteDBService';
import { useCollectionStore } from '@/features/collections/stores/useCollectionStore';
import { Page } from '@/features/pages/types/page.types';

const SharedCollectionNote = () => {
  const pageId = (useParams<{ pageId: string }>().pageId as string) || '';
  const setCurrentPage = usePageStore(state => state.setCurrentPage);
  const currentCollection = useCollectionStore(state => state.currentCollection);

  const findCurrentPage = useCallback(
    (pageId: string) => {
      if (!currentCollection) return null;
      return (currentCollection.structure || [])
        .flatMap(s => s.section.pages)
        .find(p => p.id === pageId);
    },
    [currentCollection]
  );

  const {
    data: page,
    isError,
    isLoading,
  } = useQuery({
    queryKey: [`note/${pageId}`],
    queryFn: async () => {
      const alreadyLoadedPage = findCurrentPage(pageId);
      if (alreadyLoadedPage) {
        return alreadyLoadedPage as Page;
      }
      return remoteDbService.getPublicNote(pageId);
    },
  });

  useEffect(() => {
    if (!page) return;
    setCurrentPage(page);
  }, [page, setCurrentPage]);

  if (isError || !pageId) {
    return <div>Error</div>;
  }

  // If loading is still happening after the delay, show spinner
  if (isLoading) {
    return (
      <div className="w-full h-[75%] flex items-center justify-center animate-fadeIn">
        <Spinner size={26} />
      </div>
    );
  }

  if (page?.id) {
    return (
      <div key={pageId}>
        <div className="overflow-y-auto overflow-x-hidden">
          <div className="mx-auto md:max-w-3xl lg:max-w-4xl">
            <PageTitle showTag={false} enableEditing={false} />
            <Editor key={pageId} enableEditing={false} />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full h-[75%] flex flex-col items-center justify-center">
      <div className="flex-col space-y-4 text-center">
        <div className="text-3xl text-muted-foreground">¯/_(ツ)_/¯</div>
        <div className="text-xl text-muted-foreground">No notes found here</div>
      </div>
    </div>
  );
};

export default memo(SharedCollectionNote);
